import { useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Ticket } from "../../types/entities/ticket"
import CancelOrderDialog, { CancelOrderDialogHandle } from "../../components/CancelOrderDialog"
import PaymentHoldBanner from "../../components/PaymentHoldBanner"
import TicketHallModal from "./TicketHallModal"
import TicketQrModal from "./TicketQrModal"
import { fmtRub, formatOrderNumber, formatSessionDateTime, formatStatusLabel, getTicketStatusColor, isPaid, isUnpaid } from "./ticketUtils"
import UnpaidOrderPayment from "./UnpaidOrderPayment"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type RowProps = {
    label: string
    value: string
    accent?: string
}

function Row({ label, value, accent }: RowProps) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-[13px] text-white/50" style={{ fontFamily: BODY }}>
                {label}
            </span>
            <span
                className="shrink-0 text-right"
                style={{
                    fontFamily: MONO,
                    fontWeight: 700,
                    fontSize: 13,
                    color: accent ?? "#fff",
                }}>
                {value}
            </span>
        </div>
    )
}

export function TicketItem({
    ticket,
    index = 0,
    onCancel,
    cancelLoading,
    highlighted = false,
    onRefresh,
}: {
    ticket: Ticket
    index?: number
    onCancel: (id: string | number, statusId: string | number) => void
    cancelLoading: string | number | null
    highlighted?: boolean
    onRefresh?: () => void
}) {
    const [expanded, setExpanded] = useState(isUnpaid(ticket))
    const [qrOpen, setQrOpen] = useState(false)
    const [hallOpen, setHallOpen] = useState(false)
    const cancelRef = useRef<CancelOrderDialogHandle>(null)

    const statusColor = getTicketStatusColor(ticket.status.id, ticket.status.name)
    const session = formatSessionDateTime(ticket.session.datetime_at)
    const poster = ticket.film.poster_mobile?.big
    const cancelling = cancelLoading === ticket.id

    const onCancelClick = () => {
        cancelRef.current?.open({
            unpaid: isUnpaid(ticket),
            onCancel: async () => {
                await onCancel(ticket.id, ticket.status.id)
            },
            onSuccess: onRefresh ? () => onRefresh() : undefined,
        })
    }

    return (
        <>
            <motion.article
                id={`ticket-${ticket.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`overflow-hidden rounded-[22px] border bg-white/[0.05] transition-shadow duration-500 ${
                    highlighted ? "border-[#349be6] shadow-[0_0_24px_rgba(52,155,230,0.35)]" : "border-white/10"
                }`}>
                {/* Poster hero */}
                <button
                    type="button"
                    onClick={() => setExpanded(v => !v)}
                    className="relative block w-full cursor-pointer text-left">
                    <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#0d1528]">
                        {poster ? (
                            <img src={poster} alt="" className="absolute inset-0 size-full object-cover" />
                        ) : (
                            <div
                                className="absolute inset-0"
                                style={{ background: "linear-gradient(135deg, #e4206b 0%, #349be6 100%)" }}
                            />
                        )}
                        <div
                            className="absolute inset-0"
                            style={{
                                background: "linear-gradient(to top, rgba(10,18,42,0.95) 0%, rgba(10,18,42,0.35) 55%, transparent 100%)",
                            }}
                        />
                        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-4 md:p-5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h3
                                        className="text-white leading-snug line-clamp-2"
                                        style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 17, letterSpacing: "-0.01em" }}>
                                        {ticket.film.name}
                                    </h3>
                                    <p className="mt-1 text-white/70 text-[13px]" style={{ fontFamily: MONO, fontWeight: 600 }}>
                                        {session.full}
                                    </p>
                                </div>
                                <span
                                    className="shrink-0 rounded-full px-3 py-1 text-[11px]"
                                    style={{
                                        fontFamily: MONO,
                                        fontWeight: 700,
                                        color: statusColor,
                                        background: `${statusColor}18`,
                                        border: `1px solid ${statusColor}40`,
                                    }}>
                                    {formatStatusLabel(ticket.status.name)}
                                </span>
                            </div>
                        </div>
                    </div>
                </button>

                {/* Unpaid timer banner */}
                {isUnpaid(ticket) && (
                    <PaymentHoldBanner
                        createdAt={ticket.created_at}
                        expiredAt={ticket.expired_at}
                        className="mx-4 mt-4"
                    />
                )}

                <AnimatePresence initial={false}>
                    {expanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden">
                            <div className="flex flex-col gap-4 border-t border-white/[0.08] p-4 md:p-5">
                                <div className="rounded-[14px] border border-white/[0.07] bg-white/[0.03] px-4 py-3.5">
                                    <p
                                        className="text-white/35 text-[10px] uppercase tracking-[0.08em] mb-1"
                                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        Номер заказа
                                    </p>
                                    <p className="text-white text-[13px]" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        {formatOrderNumber(ticket.number)}
                                    </p>
                                    {ticket.code && (
                                        <p className="mt-1 text-white/45 text-[12px]" style={{ fontFamily: MONO }}>
                                            Код: {ticket.code}
                                        </p>
                                    )}
                                </div>

                                {/* Cinema info grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { label: "Кинотеатр", value: ticket.cinema.name },
                                        { label: "Зал", value: ticket.hall.name },
                                        { label: "Дата", value: session.date },
                                        { label: "Время", value: session.time },
                                    ].map(item => (
                                        <div
                                            key={item.label}
                                            className="rounded-[14px] border border-white/[0.07] bg-white/[0.03] px-3.5 py-3">
                                            <p
                                                className="text-white/35 text-[10px] uppercase tracking-[0.08em] mb-1"
                                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                                {item.label}
                                            </p>
                                            <p className="text-white text-[13px] leading-snug" style={{ fontFamily: BODY, fontWeight: 600 }}>
                                                {item.value}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {/* Seats */}
                                <div className="rounded-[14px] border border-white/[0.07] bg-white/[0.03] px-4 py-3.5">
                                    <p
                                        className="text-white/35 text-[10px] uppercase tracking-[0.08em] mb-2.5"
                                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        Места
                                    </p>
                                    <div className="flex flex-col gap-2">
                                        {ticket.seats.map((seat, i) => (
                                            <div key={i} className="flex items-center justify-between gap-3">
                                                <span className="text-white/80 text-[13px]" style={{ fontFamily: BODY }}>
                                                    Ряд{" "}
                                                    <span style={{ fontFamily: MONO, fontWeight: 700, color: "#fff" }}>{seat.row}</span>
                                                    {" · "}
                                                    Место{" "}
                                                    <span style={{ fontFamily: MONO, fontWeight: 700, color: "#fff" }}>{seat.place}</span>
                                                </span>
                                                {seat.price > 0 && (
                                                    <span className="text-white/60 text-[12px]" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                                        {fmtRub(seat.price)} ₽
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Amount */}
                                <div className="flex flex-col gap-2.5 rounded-[14px] border border-white/[0.07] bg-white/[0.03] px-4 py-3.5">
                                    <Row label="Стоимость билетов" value={`${fmtRub(ticket.amount.all)} ₽`} />
                                    {ticket.amount.bonus_pay > 0 && (
                                        <Row
                                            label={isPaid(ticket) ? "Списано бонусов" : "Спишется бонусов"}
                                            value={`−${fmtRub(ticket.amount.bonus_pay)}`}
                                            accent="#f5a623"
                                        />
                                    )}
                                    {ticket.amount.bonus_add > 0 && (
                                        <Row
                                            label={isPaid(ticket) ? "Начислено бонусов" : "Начислится бонусов"}
                                            value={`+${fmtRub(ticket.amount.bonus_add)}`}
                                            accent="#29cc6a"
                                        />
                                    )}
                                    <div className="my-0.5 h-px bg-white/[0.08]" />
                                    <Row label="Итого" value={`${fmtRub(ticket.amount.pay)} ₽`} accent="#fff" />
                                </div>

                                {ticket.description && (
                                    <p className="text-white/40 text-[12px] leading-relaxed" style={{ fontFamily: BODY }}>
                                        {ticket.description}
                                    </p>
                                )}

                                {/* Actions */}
                                <div className="flex flex-col gap-2.5 pt-1">
                                    {isUnpaid(ticket) && onRefresh && (
                                        <UnpaidOrderPayment ticket={ticket} onUpdated={onRefresh} />
                                    )}
                                    {isPaid(ticket) && (
                                        <>
                                            <motion.button
                                                type="button"
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => setQrOpen(true)}
                                                className="w-full rounded-[14px] py-3.5 text-white text-[14px]"
                                                style={{
                                                    background: "#349be6",
                                                    fontFamily: MONO,
                                                    fontWeight: 700,
                                                    boxShadow: "0 0 18px rgba(52,155,230,0.35)",
                                                }}>
                                                📱 Показать QR-код
                                            </motion.button>
                                            <motion.button
                                                type="button"
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => setHallOpen(true)}
                                                className="w-full rounded-[14px] border border-white/10 bg-white/[0.04] py-3.5 text-[14px] text-[#349be6] transition-colors hover:bg-[#349be6]/10"
                                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                                Показать места в зале
                                            </motion.button>
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        onClick={onCancelClick}
                                        disabled={cancelling}
                                        className="w-full rounded-[14px] border border-white/10 bg-white/[0.04] py-3 text-[14px] text-[#e4206b] transition-opacity hover:bg-[#e4206b]/10 disabled:opacity-50"
                                        style={{ fontFamily: BODY, fontWeight: 600 }}>
                                        {cancelling ? "Отмена..." : "Отменить билет"}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {!expanded && (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="w-full border-t border-white/[0.06] py-3 text-[13px] text-[#349be6] transition-colors hover:bg-white/[0.03]"
                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                        Подробнее ↓
                    </button>
                )}
            </motion.article>

            <CancelOrderDialog ref={cancelRef} />
            {qrOpen && (
                <TicketQrModal orderNumber={ticket.number} filmName={ticket.film.name} onClose={() => setQrOpen(false)} />
            )}
            {hallOpen && (
                <TicketHallModal
                    cinemaId={ticket.cinema.id}
                    sessionId={ticket.session.id}
                    filmName={ticket.film.name}
                    hallName={ticket.hall.name}
                    ticketSeats={ticket.seats}
                    onClose={() => setHallOpen(false)}
                />
            )}
        </>
    )
}
